// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/*
 * What wrote this file — not who.
 *
 * /Producer and /Creator are free-text strings an application stamps into a
 * PDF. They are useful and they are widely misread, so every entry here
 * carries a `benign` line saying where the string ordinarily comes from. That
 * is not softening: it is the accurate half of the information, and it is the
 * half every "is this document fake?" article leaves out.
 *
 * The clearest example is in the table below. The United States Government
 * Publishing Office publishes the Code of Federal Regulations with iText — the
 * same general-purpose library somebody fabricating a payslip would reach for.
 * A tool that says "iText means this was generated by a script" is telling you
 * something true about the string and nothing at all about the document.
 *
 * These strings are matched case-insensitively as substrings, longest pattern
 * first, so `Adobe PDF Library` wins over `Adobe`.
 */

export type ProducerCategory =
  | 'print-to-pdf'
  | 'office'
  | 'design'
  | 'library'
  | 'scanner-ocr'
  | 'pdf-editor'
  | 'typesetting'
  | 'unknown';

export interface ProducerFact {
  match: string;
  label: string;
  category: ProducerCategory;
  /** What this software is. */
  what: string;
  /** Where this string ordinarily comes from — the half that is usually missing. */
  benign: string;
}

export const CATEGORY_LABEL: Record<ProducerCategory, string> = {
  'print-to-pdf': 'Printed to PDF',
  office: 'Office application',
  design: 'Design application',
  library: 'Software library or server',
  'scanner-ocr': 'Scanner or OCR',
  'pdf-editor': 'PDF editor',
  typesetting: 'Typesetting system',
  unknown: 'Not recognised',
};

export const PRODUCERS: ProducerFact[] = [
  /* ── printed to PDF ── */
  {
    match: 'Skia/PDF',
    label: 'Chrome or Edge — print to PDF',
    category: 'print-to-pdf',
    what: 'Skia is the graphics library inside Chromium. This string means the page was printed to PDF from Chrome, Edge, or something built on them.',
    benign: 'Extremely common and entirely ordinary. Google Docs exports, most payroll and banking portals that render a statement as a web page, and anyone who pressed Ctrl+P on a page all produce this. It is also what a headless Chrome on a server produces.',
  },
  {
    match: 'Quartz PDFContext',
    label: 'macOS — print to PDF or Preview',
    category: 'print-to-pdf',
    what: 'Quartz is macOS’s drawing system. Anything saved as PDF from a Mac print dialogue, or re-saved from Preview, carries this.',
    benign: 'The normal result of a Mac user pressing ⌘P and choosing Save as PDF, or of opening any PDF in Preview and saving it. Re-saving in Preview replaces the original producer string, so this can appear on a document that was originally written by something else entirely.',
  },
  {
    match: 'Microsoft: Print To PDF',
    label: 'Windows — Microsoft Print to PDF',
    category: 'print-to-pdf',
    what: 'The PDF printer built into Windows 10 and 11.',
    benign: 'Produced whenever anyone on Windows prints anything to PDF. It carries no information about what the document originally was.',
  },
  {
    match: 'wkhtmltopdf',
    label: 'wkhtmltopdf',
    category: 'library',
    what: 'A command-line tool that renders a web page to PDF using an older WebKit engine.',
    benign: 'A very common back-end for invoices, payslips, tickets and statements generated by web applications. Its presence usually means a server built this document from a template.',
  },
  {
    match: 'Puppeteer',
    label: 'Puppeteer — headless Chrome',
    category: 'library',
    what: 'A script driving a headless Chrome to render a page to PDF.',
    benign: 'Standard modern practice for generating documents from a web template on a server.',
  },

  /* ── office ── */
  {
    match: 'Microsoft® Word',
    label: 'Microsoft Word',
    category: 'office',
    what: 'A PDF exported directly from Word.',
    benign: 'The ordinary way an offer letter, a reference, or a small employer’s payslip is made.',
  },
  {
    match: 'Microsoft® Excel',
    label: 'Microsoft Excel',
    category: 'office',
    what: 'A PDF exported directly from Excel.',
    benign: 'Very common for anything with figures in it, including payroll summaries prepared by a bookkeeper.',
  },
  {
    match: 'Acrobat PDFMaker',
    label: 'Acrobat PDFMaker (an Office add-in)',
    category: 'office',
    what: 'The Acrobat plug-in that adds a "Create PDF" button to Word, Excel and PowerPoint.',
    benign: 'Present on any document made by an Office user who has Acrobat installed. It usually appears in /Creator with an Adobe library in /Producer.',
  },
  {
    match: 'LibreOffice',
    label: 'LibreOffice',
    category: 'office',
    what: 'A PDF exported from LibreOffice or OpenOffice.',
    benign: 'The ordinary export path for anyone not using Microsoft Office, including a great many small businesses and public-sector systems.',
  },
  {
    match: 'Google',
    label: 'A Google application',
    category: 'office',
    what: 'A PDF produced by a Google product.',
    benign: 'Google Docs and Sheets exports normally carry Skia/PDF instead; this string is less common and not by itself notable.',
  },
  {
    match: 'Pages',
    label: 'Apple Pages',
    category: 'office',
    what: 'A PDF exported from Apple’s word processor.',
    benign: 'Ordinary for a document written on a Mac, iPad or iPhone.',
  },

  /* ── Adobe and design ── */
  {
    match: 'Adobe PDF Library',
    label: 'Adobe PDF Library',
    category: 'library',
    what: 'Adobe’s own PDF engine, used inside Acrobat, InDesign, Illustrator and many third-party products that license it.',
    benign: 'Appears on an enormous range of professionally produced documents. Read the /Creator alongside it — that is the field that says which application was driving the library.',
  },
  {
    match: 'Acrobat Distiller',
    label: 'Acrobat Distiller',
    category: 'pdf-editor',
    what: 'Adobe’s PostScript-to-PDF converter.',
    benign: 'Standard in print and publishing workflows, and in older corporate document pipelines.',
  },
  {
    match: 'Adobe Acrobat',
    label: 'Adobe Acrobat',
    category: 'pdf-editor',
    what: 'The document was written or re-saved by Acrobat.',
    benign: 'Acrobat stamps itself in whenever a PDF is saved from it — including after nothing more than filling in a form field, adding a comment, signing, or rotating a page.',
  },
  {
    match: 'Adobe InDesign',
    label: 'Adobe InDesign',
    category: 'design',
    what: 'A page-layout application used for anything with a designed layout.',
    benign: 'Expected on brochures, annual reports, and any professionally designed template — including the template a payroll system fills in.',
  },
  {
    match: 'Adobe Illustrator',
    label: 'Adobe Illustrator',
    category: 'design',
    what: 'A vector drawing application.',
    benign: 'Ordinary for logos, certificates and anything with drawn artwork.',
  },
  {
    match: 'Adobe Photoshop',
    label: 'Adobe Photoshop',
    category: 'design',
    what: 'An image editor. A PDF from Photoshop is usually a single image on a page.',
    benign: 'Ordinary for artwork and for scanned pages that were tidied up. Worth reading alongside whether the pages are images rather than text.',
  },
  {
    match: 'Canva',
    label: 'Canva',
    category: 'design',
    what: 'A web-based design tool.',
    benign: 'Widely used by small businesses for letterheads, invoices and certificates.',
  },

  /* ── libraries and servers ── */
  {
    match: 'iTextSharp',
    label: 'iTextSharp (.NET)',
    category: 'library',
    what: 'The .NET edition of the iText PDF library.',
    benign: 'A standard component of business software written for Windows servers.',
  },
  {
    match: 'iText',
    label: 'iText',
    category: 'library',
    what: 'A general-purpose PDF library used by software to generate documents.',
    benign: 'The United States Government Publishing Office publishes the Code of Federal Regulations with iText. It is also what a great many banks, payroll systems and invoicing platforms use. A general-purpose library says the document was generated by software, which is what a statement or payslip is.',
  },
  {
    match: 'ReportLab',
    label: 'ReportLab (Python)',
    category: 'library',
    what: 'A Python library for generating PDFs.',
    benign: 'Common in back-office and reporting systems, and in anything built in Python.',
  },
  {
    match: 'PDFsharp',
    label: 'PDFsharp (.NET)',
    category: 'library',
    what: 'A .NET PDF library.',
    benign: 'Used inside line-of-business applications on Windows.',
  },
  {
    match: 'TCPDF',
    label: 'TCPDF (PHP)',
    category: 'library',
    what: 'A PHP library for generating PDFs.',
    benign: 'Very common in web applications, invoicing systems and content management platforms.',
  },
  {
    match: 'mPDF',
    label: 'mPDF (PHP)',
    category: 'library',
    what: 'A PHP library that turns HTML into PDF.',
    benign: 'Common in web applications that render a template to a document.',
  },
  {
    match: 'dompdf',
    label: 'dompdf (PHP)',
    category: 'library',
    what: 'A PHP library that turns HTML into PDF.',
    benign: 'Common in web applications, including many small invoicing and payroll tools.',
  },
  {
    match: 'FPDF',
    label: 'FPDF (PHP)',
    category: 'library',
    what: 'A long-established PHP PDF library.',
    benign: 'Common in older web applications.',
  },
  {
    match: 'JasperReports',
    label: 'JasperReports',
    category: 'library',
    what: 'A Java reporting engine.',
    benign: 'Standard in enterprise reporting — payroll, finance and HR systems frequently use it.',
  },
  {
    match: 'Crystal Reports',
    label: 'Crystal Reports',
    category: 'library',
    what: 'A long-established business reporting tool.',
    benign: 'Standard in enterprise finance and payroll systems.',
  },
  {
    match: 'Aspose',
    label: 'Aspose',
    category: 'library',
    what: 'A commercial document-processing library.',
    benign: 'Used inside business applications that generate or convert documents server-side.',
  },
  {
    match: 'Apache FOP',
    label: 'Apache FOP',
    category: 'library',
    what: 'A Java formatter that renders XSL-FO to PDF.',
    benign: 'Common in large institutional document pipelines, including banks and insurers.',
  },
  {
    match: 'Prince',
    label: 'Prince (PrinceXML)',
    category: 'library',
    what: 'A commercial HTML-to-PDF renderer known for high typographic quality.',
    benign: 'Used by publishers and by businesses that care about how their statements look.',
  },
  {
    match: 'pdf-lib',
    label: 'pdf-lib (JavaScript)',
    category: 'library',
    what: 'A JavaScript library that can create and modify PDFs, including in a browser.',
    benign: 'Used by web applications and by browser-based PDF tools, so it can appear on a file that was merely passed through an online utility.',
  },
  {
    match: 'PyPDF',
    label: 'pypdf (Python)',
    category: 'library',
    what: 'A Python library for reading and writing PDFs.',
    benign: 'Frequently the result of an automated step — merging, splitting, rotating or stamping — rather than of authoring.',
  },
  {
    match: 'pikepdf',
    label: 'pikepdf (Python)',
    category: 'library',
    what: 'A Python library built on QPDF for editing PDF structure.',
    benign: 'Usually indicates an automated processing step such as compression, linearisation or repair.',
  },
  {
    match: 'qpdf',
    label: 'QPDF',
    category: 'library',
    what: 'A command-line tool for transforming PDF structure.',
    benign: 'Commonly used to compress, linearise or repair a file. It rewrites the file completely, which removes any earlier versions it had.',
  },
  {
    match: 'Ghostscript',
    label: 'Ghostscript',
    category: 'library',
    what: 'A PostScript and PDF interpreter used to convert and compress documents.',
    benign: 'Very often the last step in a scanning, printing or file-size-reduction pipeline. Like QPDF, it rewrites the whole file, so anything before it is gone.',
  },
  {
    match: 'PDFium',
    label: 'PDFium',
    category: 'library',
    what: 'The PDF engine inside Chrome.',
    benign: 'Appears when a PDF has been saved back out of Chrome’s own viewer, for example after filling in a form.',
  },

  /* ── typesetting ── */
  {
    match: 'pdfTeX',
    label: 'pdfTeX (LaTeX)',
    category: 'typesetting',
    what: 'The TeX typesetting system.',
    benign: 'Expected on academic papers, theses and anything produced in LaTeX.',
  },
  {
    match: 'XeTeX',
    label: 'XeTeX (LaTeX)',
    category: 'typesetting',
    what: 'A Unicode-capable TeX engine.',
    benign: 'Expected on academic and multilingual documents.',
  },
  {
    match: 'LuaTeX',
    label: 'LuaTeX (LaTeX)',
    category: 'typesetting',
    what: 'A scriptable TeX engine.',
    benign: 'Expected on academic documents.',
  },
  {
    match: 'Typst',
    label: 'Typst',
    category: 'typesetting',
    what: 'A modern typesetting system.',
    benign: 'Increasingly common for academic and technical documents.',
  },

  /* ── scanners and OCR ── */
  {
    match: 'ABBYY',
    label: 'ABBYY FineReader',
    category: 'scanner-ocr',
    what: 'Optical character recognition software.',
    benign: 'Indicates the document was scanned from paper and then had text recognised over the image. Very common for anything an office scans and files.',
  },
  {
    match: 'Tesseract',
    label: 'Tesseract OCR',
    category: 'scanner-ocr',
    what: 'Open-source optical character recognition.',
    benign: 'Indicates a scanned document processed by an automated pipeline.',
  },
  {
    match: 'Xerox',
    label: 'A Xerox device',
    category: 'scanner-ocr',
    what: 'A Xerox scanner or multifunction printer.',
    benign: 'The document was scanned on an office machine.',
  },
  {
    match: 'Canon',
    label: 'A Canon device',
    category: 'scanner-ocr',
    what: 'A Canon scanner or multifunction printer.',
    benign: 'The document was scanned on an office machine.',
  },
  {
    match: 'Konica',
    label: 'A Konica Minolta device',
    category: 'scanner-ocr',
    what: 'A Konica Minolta scanner or multifunction printer.',
    benign: 'The document was scanned on an office machine.',
  },
  {
    match: 'RICOH',
    label: 'A Ricoh device',
    category: 'scanner-ocr',
    what: 'A Ricoh scanner or multifunction printer.',
    benign: 'The document was scanned on an office machine.',
  },
  {
    match: 'Hewlett-Packard',
    label: 'An HP device',
    category: 'scanner-ocr',
    what: 'An HP scanner or multifunction printer.',
    benign: 'The document was scanned on an office machine.',
  },
  {
    match: 'Epson',
    label: 'An Epson device',
    category: 'scanner-ocr',
    what: 'An Epson scanner.',
    benign: 'The document was scanned, very often on a home scanner.',
  },
  {
    match: 'Image Capture',
    label: 'macOS Image Capture',
    category: 'scanner-ocr',
    what: 'The scanning application built into macOS.',
    benign: 'The document was scanned on a Mac.',
  },

  /* ── PDF editors ── */
  {
    match: 'Foxit',
    label: 'Foxit',
    category: 'pdf-editor',
    what: 'A PDF reader and editor.',
    benign: 'A common Acrobat alternative. Like Acrobat, it stamps itself in after any save, including one that only filled in a form or added a signature.',
  },
  {
    match: 'Nitro',
    label: 'Nitro PDF',
    category: 'pdf-editor',
    what: 'A PDF editor.',
    benign: 'Widely deployed in businesses as an Acrobat alternative.',
  },
  {
    match: 'PDF-XChange',
    label: 'PDF-XChange',
    category: 'pdf-editor',
    what: 'A PDF viewer and editor.',
    benign: 'A common free viewer; it re-saves the file after annotation or form filling.',
  },
  {
    match: 'Smallpdf',
    label: 'Smallpdf',
    category: 'pdf-editor',
    what: 'An online PDF service.',
    benign: 'The file passed through an online tool — typically to compress it, merge it, or convert it. That step rewrites the file.',
  },
  {
    match: 'iLovePDF',
    label: 'iLovePDF',
    category: 'pdf-editor',
    what: 'An online PDF service.',
    benign: 'The file passed through an online tool. That step rewrites the file and removes any earlier versions.',
  },
  {
    match: 'Sejda',
    label: 'Sejda',
    category: 'pdf-editor',
    what: 'An online and desktop PDF editor.',
    benign: 'The file was processed by a PDF utility.',
  },
  {
    match: 'Soda PDF',
    label: 'Soda PDF',
    category: 'pdf-editor',
    what: 'A PDF editor.',
    benign: 'A consumer PDF tool; its presence means the file was re-saved by it.',
  },
  {
    match: 'UPDF',
    label: 'UPDF',
    category: 'pdf-editor',
    what: 'A PDF editor.',
    benign: 'A consumer PDF tool; its presence means the file was re-saved by it.',
  },
  {
    match: 'Preview',
    label: 'macOS Preview',
    category: 'pdf-editor',
    what: 'The PDF viewer built into macOS.',
    benign: 'Saving from Preview — after signing, annotating, or even just rotating a page — rewrites the file and replaces the original producer string.',
  },
];

// Longest first, so a specific match is never shadowed by a general one.
const ORDERED = [...PRODUCERS].sort((a, b) => b.match.length - a.match.length);

export function identifyProducer(value: string | null): ProducerFact | null {
  if (!value) return null;
  const haystack = value.toLowerCase();
  for (const fact of ORDERED) {
    if (haystack.includes(fact.match.toLowerCase())) return fact;
  }
  return null;
}

/** How many distinct applications the whole production chain names. */
export function summariseChain(strings: Array<string | null>): string[] {
  const seen = new Map<string, string>();
  for (const s of strings) {
    if (!s) continue;
    const fact = identifyProducer(s);
    const key = fact ? fact.label : s;
    if (!seen.has(key)) seen.set(key, key);
  }
  return [...seen.keys()];
}
